import React, { useState, useEffect, useCallback, useRef } from 'react';
import FittedSignatureCanvas from '../RateAgreement/FittedSignatureCanvas';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment
} from '@mui/material';
import {
  Save as SaveIcon,
  PictureAsPdf as PdfIcon,
  Upload as UploadIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ThumbUp as ThumbUpIcon
} from '@mui/icons-material';
import DocumentSelector from '../common/DocumentSelector';
import useDocumentVersions from '../../hooks/useDocumentVersions';
import useRateCatalog from '../../hooks/useRateCatalog';
import CancelNegotiationButton from '../RateAgreement/CancelNegotiationButton';


// eslint-disable-next-line import/first
import { useUser } from '../../context/UserContext';
// eslint-disable-next-line import/first
import { settingsAccess } from '../../utils/settingsAccess';

const DUTY_LEVELS = ['light', 'medium', 'heavy', 'additional', 'all'];
const DUTY_LEVEL_LABELS = {
  light: 'Light Duty (Up to 10,000 GVWR)',
  medium: 'Medium Duty (10,000-20,000 lbs/21-30ft)',
  heavy: 'Heavy Duty (+lbs/31+ ft)',
  additional: 'Additional Charges',
  all: 'All Levels / N/A'
};




function RatesTab({ application, onUpdate, isReadOnly }) {
  // Proposing, resending, accepting and signing agreements is White Knight RM/owner work
  const { currentUser } = useUser();
  const canManageAgreements = settingsAccess(currentUser).sitesRates === 'write';
  // Rate sheets stack rather than overwrite; this drives the version selector.
  const {
    versions: rateSheetVersions,
    refresh: refreshRateSheetVersions,
    deleteVersion: deleteRateSheetVersion
  } = useDocumentVersions(application?._id);
  // The server's ordered catalog: the same rows, labels and order as
  // Settings → Rates and the rate agreement page
  const rateCatalog = useRateCatalog();
  const SERVICE_CATEGORIES = { rates: rateCatalog.rates, additionalCharges: rateCatalog.additionalCharges };
  const isMileageService = rateCatalog.isMileage;
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [rateValues, setRateValues] = useState({}); 
  const [defaultRates, setDefaultRates] = useState({});
  const [generatePdfDialogOpen, setGeneratePdfDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [submittedRateAgreement, setSubmittedRateAgreement] = useState(null);
  const [currentRateAgreement, setCurrentRateAgreement] = useState(null);
  const [negotiationDialogOpen, setNegotiationDialogOpen] = useState(false);
  const [representativeSignature, setRepresentativeSignature] = useState('');
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signatureType, setSignatureType] = useState('draw');
  const [typedSignature, setTypedSignature] = useState('');
  const signatureRef = useRef(null);


  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);


  const fetchFacilities = useCallback(async () => {
    if (!application || !application._id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/facilities`, { headers: getAuthHeader() });
      if (!response.ok) throw new Error(`Failed to fetch facilities: ${response.statusText}`);
      const data = await response.json();
      setFacilities(data);
      if (data.length > 0 && !selectedFacilityId) {
        setSelectedFacilityId(data[0]._id);
      } else if (data.length === 0) {
        setSelectedFacilityId('');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch facilities');
      console.error('Error fetching facilities:', err);
    } finally {
      setLoading(false);
    }
  }, [application, getAuthHeader, selectedFacilityId]);


  useEffect(() => {
    const fetchDefaultRates = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/rates/defaults`, { headers: getAuthHeader() });
        if (!response.ok) throw new Error(`Failed to fetch default rates: ${response.statusText}`);
        const data = await response.json();
        setDefaultRates(data);
        setRateValues(JSON.parse(JSON.stringify(data)));
      } catch (err) {
        setError(err.message || 'Failed to fetch default rates');
        console.error('Error fetching default rates:', err);
        setRateValues({});
      } finally {

      }
    };
    fetchDefaultRates();
  }, [getAuthHeader]);


  const fetchRates = useCallback(async () => {
    if (!rateCatalog.loaded) return; // rows are filtered against the catalog; wait for it
    if (!application || !application._id || !selectedFacilityId || Object.keys(defaultRates).length === 0) {

      setRateValues(JSON.parse(JSON.stringify(defaultRates))); 
      setLoading(false);
      return; 
    }

    setLoading(true);
    setError(null);


    const selectedFacility = facilities.find(f => f._id === selectedFacilityId);
    const facilityServices = selectedFacility?.services || {};
    console.log('[RatesTab] Selected Facility Services:', facilityServices);

    let currentRates = JSON.parse(JSON.stringify(defaultRates));


    // Keep a rate combo only when the facility offers the matching service
    // (per duty level). Mileage components, GOA, universal charges, and all
    // additional charges are kept for every facility. Rates that don't
    // survive this filter never get values, so unoffered services carry no
    // payable rate into agreements — those jobs must be negotiated/reassigned.
    const filteredRates = {};
    Object.entries(currentRates).forEach(([serviceTypeId, dutyLevels]) => {
        const isMileageOrGoaOrAdditional = rateCatalog.isMileage(serviceTypeId) || serviceTypeId === 'goneOnArrival' || rateCatalog.byKey[serviceTypeId]?.category === 'additionalCharges';
        if (isMileageOrGoaOrAdditional) {
            filteredRates[serviceTypeId] = dutyLevels;
            return;
        }

        const keptDuties = {};
        Object.entries(dutyLevels).forEach(([dutyLevel, values]) => {
            const requiredKeys = getFacilityServiceKeysFor(serviceTypeId, dutyLevel);
            const offered = requiredKeys === null || requiredKeys.some(key => facilityServices[key] === true);
            if (offered) keptDuties[dutyLevel] = values;
        });
        if (Object.keys(keptDuties).length > 0) {
            filteredRates[serviceTypeId] = keptDuties;
        }
    });


    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/rates`, { headers: getAuthHeader() });
      if (!response.ok) throw new Error(`Failed to fetch rates: ${response.statusText}`);
      const allRatesData = await response.json();

      const facilityRates = allRatesData.filter(rate => rate.facilityId === selectedFacilityId);


      facilityRates.forEach(rate => {
        if (!rate.serviceType || !rate.dutyLevel || !DUTY_LEVELS.includes(rate.dutyLevel)) {
          console.warn(`Skipping fetched rate due to invalid structure or dutyLevel:`, rate);
          return;
        }

        // Check if this service type exists in our known service types
        const isValidServiceType = Boolean(rateCatalog.byKey[rate.serviceType]);
        if (!isValidServiceType) {
          console.warn(`Skipping unknown serviceType '${rate.serviceType}'`);
          return;
        }

        // Only merge saved rates into combos the facility currently offers —
        // a rate saved when the facility offered a service must disappear if
        // that service was since deselected.
        if (filteredRates[rate.serviceType] && filteredRates[rate.serviceType][rate.dutyLevel] !== undefined) {
          filteredRates[rate.serviceType][rate.dutyLevel] = {
            rate: rate.rate ?? '',
            freeMiles: rate.freeMiles ?? ''
          };
        } else if (isValidServiceType) {
          console.info(`Rate for '${rate.serviceType}/${rate.dutyLevel}' skipped: service no longer offered by the selected facility.`);
        }
      });

      setRateValues(filteredRates);
    } catch (err) {
      setError(err.message || 'Failed to fetch or merge facility rates');
      console.error('Error fetching/merging rates:', err);

      setRateValues(filteredRates);
    } finally {
      setLoading(false);
    }
  }, [application, selectedFacilityId, getAuthHeader, defaultRates, facilities, rateCatalog]);

  // Any agreement still open for the selected facility (either flow), so an
  // accidental "Generate" can be withdrawn
  const [pendingAgreement, setPendingAgreement] = useState(null);
  const fetchPendingAgreement = useCallback(async () => {
    if (!selectedFacilityId) {
      setPendingAgreement(null);
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/facilities/${selectedFacilityId}/agreement-status`, { headers: getAuthHeader() });
      const data = response.ok ? await response.json() : null;
      setPendingAgreement(data?.status === 'Pending' && data.agreementId ? data : null);
    } catch {
      setPendingAgreement(null);
    }
  }, [selectedFacilityId, getAuthHeader]);


  const fetchSubmittedRateAgreement = useCallback(async () => {
    if (!application || !application._id || !selectedFacilityId) {
      setSubmittedRateAgreement(null);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/applications/${application._id}/rate-agreement/submitted-rates/${selectedFacilityId}`,
        { headers: getAuthHeader() }
      );
      
      if (response.status === 404) {

        setSubmittedRateAgreement(null);
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to fetch submitted rate agreement: ${response.statusText}`);
      }
      
      const data = await response.json();
      setSubmittedRateAgreement(data.rateAgreement);
    } catch (err) {
      console.error('Error fetching submitted rate agreement:', err);

      setSubmittedRateAgreement(null);
    }
  }, [application, selectedFacilityId, getAuthHeader]);


  const fetchCurrentRateAgreement = useCallback(async () => {
    if (!application || !application._id || !selectedFacilityId) {
      setCurrentRateAgreement(null);
      return;
    }

    try {

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/applications/${application._id}/rate-agreement/current/${selectedFacilityId}`,
        { headers: getAuthHeader() }
      );
      
      if (response.status === 404) {

        setCurrentRateAgreement(null);
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to fetch current rate agreement: ${response.statusText}`);
      }
      
      const data = await response.json();
      setCurrentRateAgreement(data.rateAgreement);
    } catch (err) {
      console.error('Error fetching current rate agreement:', err);

      setCurrentRateAgreement(null);
    }
  }, [application, selectedFacilityId, getAuthHeader]);


  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);


  useEffect(() => {
    if (Object.keys(defaultRates).length > 0) {
        fetchRates(); 
    }
  }, [selectedFacilityId, defaultRates, fetchRates]);


  useEffect(() => {
    fetchSubmittedRateAgreement();
    fetchCurrentRateAgreement();
    fetchPendingAgreement();
  }, [selectedFacilityId, fetchSubmittedRateAgreement, fetchCurrentRateAgreement, fetchPendingAgreement]);


  const handleFacilityChange = (event) => {
    const newFacilityId = event.target.value;
    // Validate that the selected facility exists
    if (newFacilityId === '' || facilities.some(f => f._id === newFacilityId)) {
      setSelectedFacilityId(newFacilityId);
      // Clear any existing errors when changing facilities
      setError(null);
      setSuccess(null);
    } else {
      console.warn(`Invalid facility ID selected: ${newFacilityId}`);
    }
  };


  const handleRateChange = (serviceTypeId, dutyLevel, field, value) => {
    // Validate inputs
    if (!serviceTypeId || !dutyLevel || !field) {
      console.warn('Invalid parameters for handleRateChange:', { serviceTypeId, dutyLevel, field });
      return;
    }

    // Validate duty level
    if (!DUTY_LEVELS.includes(dutyLevel)) {
      console.warn(`Invalid duty level '${dutyLevel}' for service '${serviceTypeId}'`);
      return;
    }

    // Validate field
    if (!['rate', 'freeMiles'].includes(field)) {
      console.warn(`Invalid field '${field}' for rate change`);
      return;
    }

    setRateValues(prev => {
      const newState = JSON.parse(JSON.stringify(prev));
      if (!newState[serviceTypeId]) {
        newState[serviceTypeId] = {};
      }
      if (!newState[serviceTypeId][dutyLevel]) {
        newState[serviceTypeId][dutyLevel] = { rate: '', freeMiles: '' };
      }
      newState[serviceTypeId][dutyLevel][field] = value;
      return newState;
    });
  };


  const handleSaveRates = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const rateData = [];
      Object.entries(rateValues).forEach(([serviceTypeId, dutyLevels]) => {
        Object.entries(dutyLevels).forEach(([dutyLevel, values]) => {

          if (values.rate !== '' || (isMileageService(serviceTypeId) && values.freeMiles !== '')) {

             if (!DUTY_LEVELS.includes(dutyLevel)) {
                 console.warn(`Skipping save for invalid dutyLevel '${dutyLevel}' on service '${serviceTypeId}'`);
                 return;
             }
            rateData.push({
              applicationId: application._id,
              facilityId: selectedFacilityId,
              serviceType: serviceTypeId,
              dutyLevel: dutyLevel,
              rate: parseFloat(values.rate) || 0,
              freeMiles: isMileageService(serviceTypeId) ? (parseFloat(values.freeMiles) || 0) : 0
            });
          }
        });
      });

      console.log('Saving Rates Payload:', JSON.stringify(rateData));



      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/rates?clearExisting=true`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify(rateData)
      });

      if (!response.ok) {
        throw new Error(`Failed to save rates: ${response.statusText}`);
      }
      setSuccess('Rates saved successfully');
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message || 'Failed to save rates');
    } finally {
      setLoading(false);
    }
  };


  const proposedChanges = (submittedRateAgreement?.rateItems || [])
    .filter(item => item.proposedRate !== null && item.proposedRate !== undefined && item.proposedRate !== item.originalRate);
  const allProposedDecided = proposedChanges.length > 0 &&
    proposedChanges.every(item => item.negotiationStatus === 'accepted' || item.negotiationStatus === 'rejected');
  const anyProposedRejected = proposedChanges.some(item => item.negotiationStatus === 'rejected');
  const anyProposedPending = proposedChanges.some(item => item.negotiationStatus === 'pending');
  const anyProposedDecided = proposedChanges.some(item => item.negotiationStatus !== 'pending');
  // Signed decisions with a rejection went back to the provider; it's their move
  const sentBackToProvider = submittedRateAgreement?.status === 'negotiated' &&
    !!submittedRateAgreement?.representativeSignature?.name;

  const handleNegotiateRates = async (acceptAll = false, rateItemId = null, status = null, finalize = false, signatureImage = null) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      if (!submittedRateAgreement) {
        throw new Error('No submitted rate agreement found');
      }
      
      const payload = {
        acceptAll: acceptAll,
        // Signing the decisions dialog completes the agreement; without this
        // the server only records the decisions and leaves it un-finalized
        ...(finalize ? { finalize: true } : {})
      };


      if (!acceptAll && rateItemId && status) {
        payload.negotiations = [{
          rateItemId,
          status
        }];
      }
      

      if (representativeSignature && signatureImage) {
        payload.representativeSignature = { name: representativeSignature, data: signatureImage };
      } else if (representativeSignature) {

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 500;
        canvas.height = 200;
        

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        

        ctx.font = '40px "Brush Script MT", cursive';
        ctx.fillStyle = 'black';
        

        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(representativeSignature, canvas.width / 2, canvas.height / 2);
        

        const signatureData = canvas.toDataURL();
        
        payload.representativeSignature = {
          name: representativeSignature,
          data: signatureData
        };
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/applications/${application._id}/rate-agreement/${submittedRateAgreement._id}/negotiate`,
        {
          method: 'PUT',
          headers: getAuthHeader(),
          body: JSON.stringify(payload)
        }
      );
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.message || `Failed to negotiate rates: ${response.statusText}`);
      }
      
      setSuccess(responseData.status === 'finalized'
        ? 'Rate agreement finalized.'
        : responseData.returnedToProvider
          ? 'Your decisions were sent back to the service provider to accept or propose new rates.'
          : 'Decision saved.');


      await fetchRates();
      await fetchSubmittedRateAgreement();
      

      setNegotiationDialogOpen(false);
      

      return responseData;
      
    } catch (err) {
      setError(err.message || 'Failed to negotiate rates');
      console.error('Error negotiating rates:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };


  const handleApplyToAllFacilities = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      console.log('[ApplyToAll] Facilities State:', facilities);
      const allRateData = [];
      facilities.forEach(facility => {
        Object.entries(rateValues).forEach(([serviceTypeId, dutyLevels]) => {
          Object.entries(dutyLevels).forEach(([dutyLevel, values]) => {
            if (values.rate !== '' || (isMileageService(serviceTypeId) && values.freeMiles !== '')) {
               if (!DUTY_LEVELS.includes(dutyLevel)) {
                 console.warn(`Skipping apply-all for invalid dutyLevel '${dutyLevel}' on service '${serviceTypeId}'`);
                 return; 
               }
              allRateData.push({
                applicationId: application._id,
                facilityId: facility._id,
                serviceType: serviceTypeId,
                dutyLevel: dutyLevel,
                rate: parseFloat(values.rate) || 0,
                freeMiles: isMileageService(serviceTypeId) ? (parseFloat(values.freeMiles) || 0) : 0
              });
            }
          });
        });
      });

      console.log('[ApplyToAll] Payload (allRateData):', allRateData);
      console.log('Applying Rates Payload:', JSON.stringify(allRateData));


      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/rates?clearExisting=true`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify(allRateData)
      });

      if (!response.ok) {
        throw new Error(`Failed to apply rates to all facilities: ${response.statusText}`);
      }
      setSuccess('Rates applied to all facilities successfully');
      if (onUpdate) onUpdate();
      fetchRates();
    } catch (err) {
      setError(err.message || 'Failed to apply rates to all facilities');
    } finally {
      setLoading(false);
    }
  };


  const handleResendEmail = async (successMessage) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!submittedRateAgreement || !selectedFacilityId) {
        throw new Error('No rate agreement found to resend email');
      }



      const payload = {
        facilityId: selectedFacilityId,
        forceRegenerate: false,
        resendEmail: true,
        rateAgreementId: submittedRateAgreement._id,
        agreementToken: submittedRateAgreement.token
      };

      console.log("Resending rate agreement email with payload:", payload);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/applications/${application._id}/rate-agreement/generate`,
        {
          method: 'POST',
          headers: getAuthHeader(),
          body: JSON.stringify(payload)
        }
      );

      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {

        responseData = { message: 'Email resend request submitted.' };
      }

      if (response.ok) {
        setSuccess(typeof successMessage === 'string' ? successMessage : 'Email sent to the service provider.');
        return responseData;
      } else {
        throw new Error(responseData.message || `Failed to resend email: ${response.statusText}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to resend rate agreement email');
      console.error('Error resending email:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };


  const handleGenerateRateSheet = async (allFacilities = false) => {
    setGeneratePdfDialogOpen(false);
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {

      const payload = {
        forceRegenerate: true,
        useDefaultRates: true,
        newAgreement: true,
        createNewAgreement: true
      };
      
      if (!allFacilities && selectedFacilityId) {

        payload.facilityId = selectedFacilityId;
      }

      console.log("Generating NEW rate agreement with payload:", payload);


      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/rate-agreement/generate`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || `Failed to generate rate agreement: ${response.statusText}`);
      }


      setSuccess(responseData.message || 'New rate agreement generated and email sent successfully.');
      

      if (responseData.url) {
        window.open(responseData.url, '_blank');
      } else if (responseData.token) {

        const baseUrl = window.location.origin;
        window.open(`${baseUrl}/rate-agreement/${responseData.token}?view=staff`, '_blank');
      }


      await fetchSubmittedRateAgreement();
      await fetchCurrentRateAgreement();

    } catch (err) {
      setError(err.message || 'Failed to generate rate agreement.');
      console.error('Error generating rate agreement:', err);
    } finally {
      setLoading(false);
    }
  };
  const handleFileChange = (event) => { setSelectedFile(event.target.files[0]); };

  // This was an empty stub, so the Upload dialog silently did nothing. The
  // backend route has been there all along; the upload archives whatever rate
  // sheet is currently on the application rather than replacing it.
  const handleUploadRateSheet = async () => {
    if (!selectedFile || !application?._id) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const formData = new FormData();
      formData.append('rateSheet', selectedFile);
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/applications/${application._id}/upload-rate-sheet`,
        {
          method: 'POST',
          // No Content-Type — the browser sets the multipart boundary
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `Upload failed (${response.status})`);
      }
      setSuccess('Rate sheet uploaded. Any previous rate sheet is still available in the version selector.');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setFileInputKey((k) => k + 1);
      await refreshRateSheetVersions();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error uploading rate sheet:', err);
      setError(err.message || 'Failed to upload rate sheet.');
    } finally {
      setLoading(false);
    }
  };
  const getSelectedFacilityName = () => {
    const facility = facilities.find(f => f._id === selectedFacilityId);
    return facility ? facility.facilityName : 'Unknown Facility';
  };



  const renderRateInput = (serviceType, dutyLevel) => {
    // Validate inputs
    if (!serviceType || !serviceType.id || !dutyLevel) {
      return <TableCell key={dutyLevel || 'unknown'}></TableCell>;
    }

    // Check if rate values exist and are properly structured
    if (!rateValues || !rateValues[serviceType.id] || !rateValues[serviceType.id][dutyLevel]) {
        return <TableCell key={dutyLevel}></TableCell>;
    }

    // Check if service applies to this duty level
    if (!serviceType.appliesTo || !Array.isArray(serviceType.appliesTo) || !serviceType.appliesTo.includes(dutyLevel)) {
      return <TableCell key={dutyLevel}></TableCell>;
    }
 
    const isGoa = serviceType.id === 'goneOnArrival';
    const rateValue = rateValues[serviceType.id]?.[dutyLevel]?.rate || '';
    const freeMilesValue = rateValues[serviceType.id]?.[dutyLevel]?.freeMiles || '';
    const showFreeMiles = isMileageService(serviceType.id);


    const commonInputProps = {
      style: { textAlign: 'right' }
    };


    const goaRateInputProps = {
      ...commonInputProps,
      inputMode: 'numeric',
      pattern: '[0-9]*',
      min: 0,
      max: 100
    };


    const generalRateInputProps = {
      ...commonInputProps,
      inputMode: 'decimal',
      pattern: '[0-9]*[.,]?[0-9]*'
    };

    return (
      <TableCell key={dutyLevel} align="center">
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <TextField
            label={isGoa ? "Rate (%)" : "Rate"}
            type="text"
            InputProps={isGoa ? {
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              inputProps: goaRateInputProps
            } : {
              inputProps: generalRateInputProps
            }}
            value={rateValue}
            onChange={(e) => handleRateChange(serviceType.id, dutyLevel, 'rate', e.target.value)}
            size="small"
            sx={{ width: '100px' }}
            variant="outlined"
            disabled={isReadOnly}
          />
          {showFreeMiles && (
            <TextField
              label="Free Miles"
              type="number"
              inputProps={{ min: 0, step: 1, style: { textAlign: 'right' } }}
              value={freeMilesValue}
              onChange={(e) => handleRateChange(serviceType.id, dutyLevel, 'freeMiles', e.target.value)}
              size="small"
              sx={{ width: '100px' }}
              variant="outlined"
              disabled={isReadOnly}
            />
          )}
        </Box>
      </TableCell>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="facility-select-label">Select Facility</InputLabel>
              <Select
                labelId="facility-select-label"
                id="facility-select"
                value={selectedFacilityId || ''}
                label="Select Facility"
                onChange={handleFacilityChange}
                disabled={isReadOnly || loading || facilities.length === 0}
              >
                <MenuItem value="">
                  <em>Select Facility</em>
                </MenuItem>
                {facilities.map((facility) => (
                  <MenuItem key={facility._id} value={facility._id}>
                    {facility.facilityName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleApplyToAllFacilities}
                disabled={isReadOnly || loading || !selectedFacilityId || facilities.length <= 1}
                startIcon={<SaveIcon />}
              >
                Apply to All Facilities
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    if (currentRateAgreement && currentRateAgreement.token) {

                      const baseUrl = window.location.origin;
                      window.open(`${baseUrl}/rate-agreement/${currentRateAgreement.token}?view=staff`, '_blank');
                    } else {
                      setError('No current rate agreement available. Generate and finalize a rate agreement first.');
                    }
                  }}
                  startIcon={<PdfIcon />}
                  disabled={!currentRateAgreement}
                >
                  {currentRateAgreement && currentRateAgreement.status === 'finalized'
                    ? "View Rate Agreement"
                    : "View Current Rate Agreement"}
                </Button>
                {canManageAgreements && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setGeneratePdfDialogOpen(true)}
                    disabled={isReadOnly || loading || !selectedFacilityId}
                    startIcon={<PdfIcon />}
                  >
                    Generate New Rate Agreement
                  </Button>
                )}
                {canManageAgreements && pendingAgreement && (
                  <CancelNegotiationButton
                    agreementId={pendingAgreement.agreementId}
                    disabled={isReadOnly || loading}
                    onCanceled={async () => {
                      setPendingAgreement(null);
                      setSubmittedRateAgreement(null);
                      setSuccess('Negotiation canceled. The service provider was notified; you can generate a new rate agreement at any time.');
                      await fetchSubmittedRateAgreement();
                      await fetchCurrentRateAgreement();
                      await fetchPendingAgreement();
                    }}
                  />
                )}
              </Box>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setUploadDialogOpen(true)}
                disabled={isReadOnly || loading}
                startIcon={<UploadIcon />}
              >
                Upload Rate Sheet
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Uploading or regenerating a rate sheet archives the previous one
          rather than discarding it — step through them here. */}
      {(rateSheetVersions.rateSheet || []).length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Rate sheets on file</Typography>
          <DocumentSelector
            versions={rateSheetVersions.rateSheet}
            label="Rate sheet"
            isReadOnly={isReadOnly}
            onDelete={async (v) => { await deleteRateSheetVersion('rateSheet', v.path); }}
          />
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box>}

      {selectedFacilityId ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Rates for {getSelectedFacilityName()}</Typography>
          <Divider sx={{ mb: 2 }} />

          {}
          {submittedRateAgreement && submittedRateAgreement.status !== 'finalized' && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                <Typography variant="h6" color="primary">
                  {sentBackToProvider ? 'Waiting for the Service Provider' : 'Proposed Rates from Service Provider'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PdfIcon />}
                    onClick={() => {
                      if (submittedRateAgreement && submittedRateAgreement.token) {
                        window.open(`${window.location.origin}/rate-agreement/${submittedRateAgreement.token}?view=staff`, '_blank');
                      } else {
                        setError('Rate agreement link not available');
                      }
                    }}
                  >
                    View Agreement
                  </Button>
                  {!canManageAgreements ? null : sentBackToProvider ? (
                    <Button variant="outlined" color="primary" onClick={handleResendEmail} disabled={loading}>
                      Resend Email to Service Provider
                    </Button>
                  ) : (
                    <>
                      {anyProposedPending && (
                        <Button
                          variant="contained"
                          color={anyProposedRejected ? 'warning' : 'success'}
                          startIcon={<ThumbUpIcon />}
                          onClick={() => setNegotiationDialogOpen(true)}
                        >
                          {anyProposedRejected
                            ? 'Accept the Rest, Sign & Send Back'
                            : anyProposedDecided ? 'Accept the Rest & Sign' : 'Accept All & Sign'}
                        </Button>
                      )}
                      {allProposedDecided && (
                        <Button
                          variant="contained"
                          color={anyProposedRejected ? 'warning' : 'success'}
                          onClick={() => setSignDialogOpen(true)}
                        >
                          {anyProposedRejected ? 'Sign & Send Back to Provider' : 'Accept & Sign'}
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </Box>

              <Typography variant="body2" sx={{ mb: 2 }}>
                {sentBackToProvider
                  ? 'You sent your decisions back. The service provider can accept them and sign, which finalizes the agreement, or propose new rates for you to review.'
                  : anyProposedRejected && allProposedDecided
                    ? 'You declined at least one proposed rate, so this is a counter-offer. Signing sends it back to the service provider; it is not final until they accept.'
                    : 'Accept or reject each proposed rate. When every rate is accepted, signing finalizes the agreement. If you reject any, signing sends your decisions back to the service provider.'}
              </Typography>
              
              <Box sx={{ mb: 2, display: sentBackToProvider ? 'none' : 'block' }}>
                <Typography variant="subtitle2">
                  Signed by: {submittedRateAgreement.serviceProviderSignature?.name || 'Unknown'}
                </Typography>
                <Typography variant="body2">
                  Date: {submittedRateAgreement.serviceProviderSignature?.date
                    ? new Date(submittedRateAgreement.serviceProviderSignature.date).toLocaleDateString()
                    : 'Unknown'}
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Proposed Rate Changes:</Typography>
              
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                      <TableCell>Service</TableCell>
                      <TableCell>Duty Level</TableCell>
                      <TableCell align="right">Original Rate ($)</TableCell>
                      <TableCell align="right">Proposed Rate ($)</TableCell>
                      <TableCell align="right">Change</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rateCatalog.sortItems(submittedRateAgreement.rateItems)
                      .filter(item => item.proposedRate !== null && item.proposedRate !== item.originalRate)
                      .map(item => {
                        const change = item.proposedRate - item.originalRate;
                        const percentChange = (change / item.originalRate) * 100;
                        const isPending = item.negotiationStatus === 'pending';
                        const isAccepted = item.negotiationStatus === 'accepted';
                        const isRejected = item.negotiationStatus === 'rejected';
                        
                        return (
                          <TableRow
                            key={item._id}
                            sx={{
                              backgroundColor: isAccepted ? 'rgba(76, 175, 80, 0.1)' : isRejected ? 'rgba(244, 67, 54, 0.1)' : 'inherit'
                            }}
                          >
                            <TableCell>{rateCatalog.labelOf(item.serviceType)}</TableCell>
                            <TableCell>{DUTY_LEVEL_LABELS[item.dutyLevel] || item.dutyLevel}</TableCell>
                            <TableCell align="right">${item.originalRate.toFixed(2)}</TableCell>
                            <TableCell align="right">
                              {item.serviceType === 'goneOnArrival' ? `${item.proposedRate.toFixed(1)}%` : `$${item.proposedRate.toFixed(2)}`}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: change > 0 ? 'error.main' : 'success.main',
                                fontWeight: 'bold'
                              }}
                            >
                              {change > 0 ? '+' : ''}{change.toFixed(2)} ({percentChange.toFixed(1)}%)
                            </TableCell>
                            <TableCell align="center">
                              {isPending && !sentBackToProvider ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    startIcon={<CheckIcon />}
                                    onClick={() => handleNegotiateRates(false, item._id, 'accepted')}
                                  >
                                    Accept
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    startIcon={<CloseIcon />}
                                    onClick={() => handleNegotiateRates(false, item._id, 'rejected')}
                                  >
                                    Reject
                                  </Button>
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: isAccepted ? 'success.main' : 'error.main',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {isAccepted ? 'Accepted' : isRejected ? 'Rejected' : 'Pending'}
                                  </Typography>
                                  {!sentBackToProvider && <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => handleNegotiateRates(false, item._id, 'pending')}
                                    title="Undo decision"
                                  >
                                    Undo
                                  </Button>}
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Finalized Rate Agreement Section */}
          {submittedRateAgreement && submittedRateAgreement.status === 'finalized' && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#e8f5e8', border: '1px solid #4caf50' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" color="success.main">
                  ✓ Finalized Rate Agreement
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PdfIcon />}
                    onClick={() => {
                      if (submittedRateAgreement && submittedRateAgreement.token) {
                        const baseUrl = window.location.origin;
                        window.open(`${baseUrl}/rate-agreement/${submittedRateAgreement.token}?view=staff`, '_blank');
                      } else {
                        setError('Rate agreement link not available');
                      }
                    }}
                  >
                    View Finalized Agreement
                  </Button>
                  {canManageAgreements && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={handleResendEmail}
                      disabled={loading}
                    >
                      Resend Email
                    </Button>
                  )}
                </Box>
              </Box>
              
              <Typography variant="body2" sx={{ mb: 2 }}>
                This rate agreement has been finalized and is now active. All rates have been agreed upon by both parties.
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  Service Provider: {submittedRateAgreement.serviceProviderSignature?.name || 'Unknown'}
                </Typography>
                <Typography variant="body2">
                  SP Signed: {submittedRateAgreement.serviceProviderSignature?.date
                    ? new Date(submittedRateAgreement.serviceProviderSignature.date).toLocaleDateString()
                    : 'Unknown'}
                </Typography>
                {submittedRateAgreement.representativeSignature?.name && (
                  <>
                    <Typography variant="subtitle2">
                      Representative: {submittedRateAgreement.representativeSignature.name}
                    </Typography>
                    <Typography variant="body2">
                      Rep Signed: {submittedRateAgreement.representativeSignature?.date
                        ? new Date(submittedRateAgreement.representativeSignature.date).toLocaleDateString()
                        : 'Unknown'}
                    </Typography>
                  </>
                )}
              </Box>
              
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold' }}>
                Status: Agreement Finalized - No Expiration
              </Typography>
            </Paper>
          )}

          {}
          <Typography variant="h6" sx={{ mt: 2, mb: 1, background: '#eee', p: 1 }}>Rates</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                  <TableCell>Services</TableCell>
                  {DUTY_LEVELS.filter(l => l !== 'additional').map(level => (
                     <TableCell key={level} align="center">{DUTY_LEVEL_LABELS[level]}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Only render rows for services the selected facility offers
                    (rateValues is pre-filtered by facility services) */}
                {SERVICE_CATEGORIES.rates
                  .filter(serviceType => rateValues[serviceType.id] && Object.keys(rateValues[serviceType.id]).length > 0)
                  .map((serviceType) => (
                  <TableRow key={serviceType.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">{serviceType.label}</TableCell>
                    {DUTY_LEVELS.filter(l => l !== 'additional').map(level => renderRateInput(serviceType, level))}
                  </TableRow>
                ))}
                {SERVICE_CATEGORIES.rates.every(serviceType => !rateValues[serviceType.id] || Object.keys(rateValues[serviceType.id]).length === 0) && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        No rate rows to show — the selected facility has no services checked. Set its services on the Facilities tab first.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {}
          <Typography variant="h6" sx={{ mt: 3, mb: 1, background: '#eee', p: 1 }}>Additional Charges</Typography>
          <TableContainer component={Paper} variant="outlined">
             <Table size="small">
               <TableHead>
                 <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                   <TableCell>Charge Type</TableCell>
                   <TableCell align="center">Rate ($)</TableCell>
                   <TableCell align="center">Free Miles</TableCell>
                 </TableRow>
               </TableHead>
               <TableBody>
                 {SERVICE_CATEGORIES.additionalCharges.map((serviceType) => (
                   <TableRow key={serviceType.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                     <TableCell component="th" scope="row">{serviceType.label}</TableCell>
                     {}
                     {renderRateInput(serviceType, 'additional')}
                     {}
                     {}
                     <TableCell></TableCell> 
                     <TableCell></TableCell>
                     <TableCell></TableCell>
                     <TableCell></TableCell>
                   </TableRow>
                 ))}
               </TableBody>
             </Table>
           </TableContainer>


          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveRates}
              disabled={isReadOnly || loading}
              startIcon={<SaveIcon />}
            >
              Save Rates for {getSelectedFacilityName()}
            </Button>
          </Box>
        </Paper>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            {facilities.length === 0
              ? 'No facilities found. Please add facilities in the Facilities tab first.'
              : 'Please select a facility to manage rates.'}
          </Typography>
        </Paper>
      )}

      {}
      <Dialog
        open={generatePdfDialogOpen}
        onClose={() => setGeneratePdfDialogOpen(false)}
      >
        <DialogTitle>Generate Rate Agreement</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will create a rate agreement for the selected facility and send an email with a link to the Service Provider.
            The Service Provider can review the rates, propose changes, and electronically sign the agreement.
            Would you like to generate a rate agreement for the selected facility or for all facilities?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGeneratePdfDialogOpen(false)} color="primary">Cancel</Button>
          <Button onClick={() => handleGenerateRateSheet(false)} color="primary" disabled={isReadOnly || loading}>Selected Facility</Button>
          <Button onClick={() => handleGenerateRateSheet(true)} color="primary" disabled={isReadOnly || loading}>All Facilities</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>Upload Rate Sheet</DialogTitle>
        <DialogContent>
          <DialogContentText>Please select a signed rate sheet PDF to upload.</DialogContentText>
          <Box sx={{ mt: 2 }}><input key={fileInputKey} accept="application/pdf" type="file" onChange={handleFileChange} /></Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} color="primary">Cancel</Button>
          <Button onClick={handleUploadRateSheet} color="primary" disabled={isReadOnly || loading || !selectedFile}>Upload</Button>
        </DialogActions>
      </Dialog>

      {}
      <Dialog open={negotiationDialogOpen} onClose={() => setNegotiationDialogOpen(false)}>
        <DialogTitle>{anyProposedDecided ? 'Accept the Remaining Proposed Rates' : 'Accept All Proposed Rates'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {anyProposedRejected
              ? 'Every proposed rate you have not decided on yet will be accepted. The rates you rejected stay rejected, so this is a counter-offer: your signature goes on it and the service provider is emailed to accept and sign, or to propose new rates. The agreement is not final until they accept.'
              : 'Every proposed rate you have not decided on yet will be accepted. Signing finalizes the agreement, puts the new rates into effect, and emails the service provider the final agreement.'}
          </DialogContentText>
          
          {}
          <Box sx={{ mt: 2, mb: 2, display: 'flex', gap: 2 }}>
            <Button
              variant={signatureType === 'draw' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setSignatureType('draw')}
            >
              Draw Signature
            </Button>
            <Button
              variant={signatureType === 'type' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setSignatureType('type')}
            >
              E-Signature
            </Button>
          </Box>
          
          <TextField
            margin="dense"
            label="Print Name"
            type="text"
            fullWidth
            value={representativeSignature}
            onChange={(e) => setRepresentativeSignature(e.target.value)}
            required
          />
          
          {signatureType === 'draw' ? (
            <Box sx={{ mt: 2, mb: 2, border: '1px solid #ccc', height: '200px', position: 'relative' }}>
              <FittedSignatureCanvas ref={signatureRef} height={200} />
              <Button
                variant="outlined"
                size="small"
                sx={{ position: 'absolute', bottom: 5, right: 5 }}
                onClick={() => signatureRef.current?.clear()}
              >
                Clear
              </Button>
            </Box>
          ) : (
            <Box sx={{ mt: 2, mb: 2 }}>
              <TextField
                label="Type your signature"
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                fullWidth
                margin="normal"
                required
                InputProps={{
                  sx: {
                    fontFamily: '"Brush Script MT", cursive',
                    fontSize: '24px'
                  }
                }}
              />
              <Box
                sx={{
                  height: '100px',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: '"Brush Script MT", cursive',
                  fontSize: '32px',
                  border: '1px solid #ccc',
                  backgroundColor: '#f9f9f9'
                }}
              >
                {typedSignature || 'Your signature will appear here'}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNegotiationDialogOpen(false)} color="primary">Cancel</Button>
          <Button
            onClick={() => {

              let signatureData;
              if (signatureType === 'draw') {
                if (!signatureRef.current || signatureRef.current.isEmpty()) {
                  setError('Please sign the agreement');
                  return;
                }
                signatureData = signatureRef.current.toDataURL();
              } else {
                if (!typedSignature.trim()) {
                  setError('Please enter your e-signature');
                  return;
                }

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = 500;
                canvas.height = 200;
                

                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                

                ctx.font = '40px "Brush Script MT", cursive';
                ctx.fillStyle = 'black';
                

                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);
                

                signatureData = canvas.toDataURL();
              }
              

              handleNegotiateRates(true, null, null, false, signatureData)
                .then(result => handleResendEmail(result?.status === 'finalized'
                  ? 'Rate agreement finalized. The service provider was emailed the final rates.'
                  : 'Sent back to the service provider. They were emailed to accept your decisions or propose new rates.'))
                .catch(() => {});
            }}
            color="success"
            variant="contained"
            disabled={!representativeSignature.trim() || loading}
          >
            {anyProposedRejected ? 'Sign & Send Back' : 'Accept & Sign'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {}
      <Dialog open={signDialogOpen} onClose={() => setSignDialogOpen(false)}>
        <DialogTitle>{anyProposedRejected ? 'Sign & Send Back to Provider' : 'Accept & Sign Rate Agreement'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {anyProposedRejected
              ? 'You declined at least one proposed rate. Your signature goes on this counter-offer: accepted rates use the proposed values and declined rates return to the original values. The service provider is emailed to accept and sign, or to propose new rates. The agreement is not final until they accept.'
              : 'You accepted every proposed rate. Signing finalizes the agreement, puts the new rates into effect, and emails the service provider the final agreement.'}
          </DialogContentText>
          
          {}
          <Box sx={{ mt: 2, mb: 2, display: 'flex', gap: 2 }}>
            <Button
              variant={signatureType === 'draw' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setSignatureType('draw')}
            >
              Draw Signature
            </Button>
            <Button
              variant={signatureType === 'type' ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => setSignatureType('type')}
            >
              E-Signature
            </Button>
          </Box>
          
          <TextField
            margin="dense"
            label="Print Name"
            type="text"
            fullWidth
            value={representativeSignature}
            onChange={(e) => setRepresentativeSignature(e.target.value)}
            required
          />
          
          {signatureType === 'draw' ? (
            <Box sx={{ mt: 2, mb: 2, border: '1px solid #ccc', height: '200px', position: 'relative' }}>
              <FittedSignatureCanvas ref={signatureRef} height={200} />
              <Button
                variant="outlined"
                size="small"
                sx={{ position: 'absolute', bottom: 5, right: 5 }}
                onClick={() => signatureRef.current?.clear()}
              >
                Clear
              </Button>
            </Box>
          ) : (
            <Box sx={{ mt: 2, mb: 2 }}>
              <TextField
                label="Type your signature"
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                fullWidth
                margin="normal"
                required
                InputProps={{
                  sx: {
                    fontFamily: '"Brush Script MT", cursive',
                    fontSize: '24px'
                  }
                }}
              />
              <Box
                sx={{
                  height: '100px',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: '"Brush Script MT", cursive',
                  fontSize: '32px',
                  border: '1px solid #ccc',
                  backgroundColor: '#f9f9f9'
                }}
              >
                {typedSignature || 'Your signature will appear here'}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignDialogOpen(false)} color="primary">Cancel</Button>
          <Button
            onClick={async () => {

              let signatureData;
              if (signatureType === 'draw') {
                if (!signatureRef.current || signatureRef.current.isEmpty()) {
                  setError('Please sign the agreement');
                  return;
                }
                signatureData = signatureRef.current.toDataURL();
              } else {
                if (!typedSignature.trim()) {
                  setError('Please enter your e-signature');
                  return;
                }

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = 500;
                canvas.height = 200;
                

                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                

                ctx.font = '40px "Brush Script MT", cursive';
                ctx.fillStyle = 'black';
                

                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);
                

                signatureData = canvas.toDataURL();
              }
              

              try {

                const result = await handleNegotiateRates(false, null, null, true, signatureData);
                await handleResendEmail(result?.status === 'finalized'
                  ? 'Rate agreement finalized. The service provider was emailed the final rates.'
                  : 'Sent back to the service provider. They were emailed to accept your decisions or propose new rates.');
                setSignDialogOpen(false);
              } catch (err) {
                console.error("Error during sign & finalize:", err);

              }
            }}
            color={anyProposedRejected ? 'warning' : 'success'}
            variant="contained"
            disabled={!representativeSignature.trim() || loading}
          >
            {anyProposedRejected ? 'Sign & Send Back' : 'Accept & Sign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


// Rate service types map to FACILITY schema service keys (the Facility model
// uses names like 'roadsideJumpStartsLightDuty'), per duty level. A rate cell
// is shown only when the facility offers at least one of the mapped services.
// Returning null means "always shown" (mileage components, GOA, universal
// charges like labor/waiting/standby, and every additional charge).
// NOTE: the previous version mapped to APPLICATION-style keys
// ('jumpStartLight'), which never exist on facility documents — the check
// failed for every service, hiding all main rate rows as empty cells.
function getFacilityServiceKeysFor(rateServiceType, dutyLevel) {
  const roadsideSuffix = { light: 'LightDuty', medium: 'MediumDuty', heavy: 'HeavyDuty' }[dutyLevel];
  const towingKeyByDuty = { light: 'lightDutyTowing', medium: 'mediumDutyTowing', heavy: 'heavyDutyTowing' }[dutyLevel];
  const allJumpStarts = ['roadsideJumpStartsLightDuty', 'roadsideJumpStartsMediumDuty', 'roadsideJumpStartsHeavyDuty'];

  switch (rateServiceType) {
    // Roadside services, per duty
    case 'jumpStart': return roadsideSuffix ? [`roadsideJumpStarts${roadsideSuffix}`] : null;
    case 'tireChange': {
      if (!roadsideSuffix) return null;
      const keys = [`roadsideTireChanges${roadsideSuffix}`];
      if (dutyLevel === 'medium' || dutyLevel === 'heavy') keys.push('mediumHeavyTire');
      return keys;
    }
    // Lockouts = unlocking vehicle doors ONLY; locksmith/key making are
    // separate key services with their own rate rows
    case 'lockouts': return roadsideSuffix ? [`roadsideLockOuts${roadsideSuffix}`] : null;
    case 'locksmith': return ['locksmith'];
    case 'keyMaking': return ['keyMaking'];
    case 'fuelDelivery': return roadsideSuffix ? [`roadsideFuelDeliveries${roadsideSuffix}`] : null;
    case 'winching':
    case 'recovery': return roadsideSuffix ? [`winchOuts${roadsideSuffix}`] : null;

    // Towing, per duty
    case 'tow': return ['lightDutyTowing'];
    case 'towMedium': return ['mediumDutyTowing'];
    case 'towHeavy': return ['heavyDutyTowing'];
    case 'flatbed':
    case 'illegallyParkedTow': return towingKeyByDuty ? [towingKeyByDuty] : null;
    case 'motorcycleTow': return ['lightDutyTowing'];
    case 'accidentTow': return ['accidentSceneTowing'];
    case 'secondaryTowing': return ['secondaryTow'];

    // Other services
    case 'storage': return ['storageFacility'];
    case 'minorRoadService': return ['mobileMechanic'];
    case 'batteryTest':
    case 'batteryReplacement': return [...allJumpStarts, 'mobileMechanic'];

    // Universal billing components: always available
    case 'labor':
    case 'waitingTime':
    case 'standbyTime':
      return null;

    default:
      return null; // Unknown/mileage/GOA/additional: always shown
  }
}

export default RatesTab;
